import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { videos, categories } from '@/mocks/videos';

const categoryMap: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  dongman: '动漫',
  jilupian: '纪录片',
  shaoer: '少儿',
};

export default function CategoryPage() {
  const { slug = 'dianying' } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  useEffect(() => {
    setActiveCategory(slug);
    setCurrentPage(1);
  }, [slug]);

  const categoryName = categoryMap[activeCategory] || '电影';
  const filteredVideos = videos.filter((v) => v.categorySlug === activeCategory);
  const totalPages = Math.ceil(filteredVideos.length / pageSize);
  const paginatedVideos = filteredVideos.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': `${categoryName} - 蘑菇视频`,
    'description': `蘑菇视频${categoryName}频道，提供最新最全的${categoryName}免费在线观看，1080P高清画质，无广告流畅播放。`,
    'url': `https://mogushipin.com/category/${activeCategory}`,
  };

  return (
    <>
      <SEOHead
        title={`${categoryName} - 蘑菇视频 | 高清${categoryName}免费在线观看 1080P无广告`}
        description={`蘑菇视频${categoryName}频道，聚合优质${categoryName}资源，提供高清${categoryName}免费在线观看，1080P画质，无广告流畅播放，每日更新最新${categoryName}。`}
        keywords={`蘑菇视频,${categoryName},${categoryName}在线观看,高清${categoryName},免费${categoryName}`}
        canonicalUrl={`https://mogushipin.com/category/${activeCategory}`}
        schema={schema}
      />

      <Navbar />

      <main className="pt-20 pb-10 min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: categoryName },
            ]}
          />

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
            {categoryName}
          </h1>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Video Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {paginatedVideos.map((video) => (
              <Link
                key={video.id}
                to={`/category/${video.categorySlug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200 transition-all duration-200 hover:border-primary-200 cursor-pointer"
                data-product-shop
              >
                <div className="relative w-full aspect-video overflow-hidden bg-background-200">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    title={`${video.title} - 蘑菇视频${categoryName}高清在线观看`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/75 text-background-50 text-xs rounded-md font-medium">
                    {video.duration}
                  </span>
                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-background-50 text-xs rounded-md font-medium">
                    {video.quality}
                  </span>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/30">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <i className="ri-play-fill text-xl"></i>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {video.views}
                    </span>
                    <span className="flex items-center gap-1 text-accent-500">
                      <i className="ri-star-fill text-xs"></i>
                      {video.rating}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* No results */}
          {paginatedVideos.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-movie-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无相关内容</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
                    currentPage === page
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}